import { Link, useParams } from "react-router-dom";
import { CalendarCheck2, ArrowLeft, MessageCircle } from "lucide-react";
import { CONDITION_CONTENT, CONTACT, PATOLOGIE } from "@/data/siteData";
import { CONDITION } from "@/constants/testIds";
import SEO from "@/components/SEO";
import { graphSchema, medicalConditionSchema, breadcrumbSchema, physicianSchema } from "@/lib/schema";

export default function ConditionPage() {
  const { slug } = useParams();
  const data = CONDITION_CONTENT[slug];
  const wa = `https://wa.me/39${CONTACT.whatsapp}`;

  if (!data) {
    return (
      <main className="pt-32 pb-24 min-h-[70vh] max-w-3xl mx-auto px-6 text-center">
        <SEO title="Pagina in preparazione" path={`/patologie/${slug}`} />
        <p className="overline">Patologia</p>
        <h1 className="font-serif text-4xl md:text-5xl mt-4 text-[#0B4A5A]">
          Pagina in preparazione
        </h1>
        <p className="mt-6 text-gray-600 leading-relaxed">
          Questa scheda dettagliata è in fase di redazione. Per informazioni
          contattaci direttamente.
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/" className="btn-secondary">
            <ArrowLeft size={16} /> Torna alla home
          </Link>
          <a
            href={CONTACT.miodottore}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            <CalendarCheck2 size={16} /> Prenota
          </a>
        </div>
      </main>
    );
  }

  const jsonLd = graphSchema([
    medicalConditionSchema(data.title, data.intro),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Patologie", path: "/#patologie" },
      { name: data.title, path: `/patologie/${slug}` },
    ]),
    physicianSchema(),
  ]);

  return (
    <main data-testid={CONDITION.root(slug)} className="pt-28">
      <SEO
        title={`${data.title} — ${data.subtitle}`}
        description={data.intro.slice(0, 155)}
        path={`/patologie/${slug}`}
        jsonLd={jsonLd}
      />
      <section className="relative py-16 md:py-24 bg-[#F9FAFB]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link
            to="/#patologie"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0B4A5A] transition"
          >
            <ArrowLeft size={14} /> Tutte le patologie
          </Link>
          <p className="overline mt-8">Patologia</p>
          <h1 className="font-serif text-4xl md:text-6xl mt-4 text-[#0B4A5A] leading-[1.05]">
            {data.title}
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">
            {data.subtitle}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="text-[17px] leading-[1.75] text-gray-800 first-letter:font-serif first-letter:text-[#0B4A5A] first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9]">
            {data.intro}
          </p>

          <div className="mt-12 space-y-12">
            {data.sections.map((s) => (
              <div key={s.h}>
                <h2 className="font-serif text-2xl md:text-3xl text-[#0B4A5A]">
                  {s.h}
                </h2>
                <div className="gold-line mt-4 w-12" />
                <p className="mt-5 text-gray-700 leading-[1.8]">{s.p}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 md:p-10 rounded-2xl bg-[#F9FAFB] border border-gray-100">
            <p className="overline">Vuoi approfondire?</p>
            <h3 className="font-serif text-2xl md:text-3xl mt-3 text-[#0B4A5A]">
              Prenota una visita specialistica.
            </h3>
            <p className="mt-3 text-gray-600">
              Un percorso diagnostico chiaro, con ascolto e attenzione.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                data-testid={CONDITION.ctaBook}
                href={CONTACT.miodottore}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <CalendarCheck2 size={16} /> Prenota
              </a>
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </div>

          <div className="mt-16">
            <p className="overline">Altre patologie</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {PATOLOGIE.filter((p) => p.slug !== slug && CONDITION_CONTENT[p.slug])
                .slice(0, 6)
                .map((p) => (
                  <Link
                    key={p.slug}
                    to={`/patologie/${p.slug}`}
                    className="text-sm px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:border-[#C6A86B] hover:text-[#0B4A5A] transition"
                  >
                    {p.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
